import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  CompanyFilter,
  Currency,
  DateTime,
  MemberContactPopover,
  OpportunityLink,
  Percent,
  Tag,
  Tooltip,
  ExportDropdown,
} from '~/components';
import CompanyLink from '~/components/CompanyLink';
import { useApi, useConfirmation, useIntegrations, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useIsMounted, useSearchParams, useSearchParamsConfig } from '~/hooks';
import opportunityStageCategories from '~/lookups/opportunity-stage-categories';
import PageLoader from '~/routes/public/pages/PageLoader';
import { colors, weights } from '~/styles';
import { QuerySort, mimeTypes } from '~/utils';
import CurrencyFilter from '~/components/filters/CurrencyFilter';
import ExportDialogAsync from '../components/ExportDialogAsync';
import FilterButton from '~/components/filters/FilterButton';
import FiltersGroup from '~/components/filters/FiltersGroup.jsx';
import OpportunityFiltersBar from '~/components/filters/OpportunityFiltersBar';
import OpportunityFiltersGroup from '~/components/filters/OpportunityFiltersGroup';
import Report from '../components/Report';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const Byline = styled.small`
  display: block;
`;

const InfoContainer = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Owner = styled.div`
  display: flex;
  align-items: center;
`;

const OwnerInfo = styled.span`
  margin-left: 0.5rem;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const TagsTitle = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

function FirstAssignment({ tags }) {
  const firstTag = tags[0];
  if (!firstTag) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <small>{firstTag.name}</small>
    </Tag>
  );
}

function Assignments({ tags }) {
  const count = tags.length - 1;
  if (count <= 0) return null;

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem' }}>
          <TagsTitle>Tags</TagsTitle>
          {tags.map((tag) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={tag.id}>
              <small>{tag.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{count}</small>
      </Tag>
    </Tooltip>
  );
}

function OpportunityDetail({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();
  const isMounted = useIsMounted();

  const [query, setQuery] = useState({ report: null, status: 'loading', loading: { table: false } });
  const [params, setParams] = useState({
    opportunityOpenedPeriod: null,
    opportunityClosedPeriod: null,
    companies: [],
    opportunityHubspotSynced: null,
    opportunitySalesforceSynced: null,
    opportunityStageCategories: [],
    opportunityStages: [],
    opportunityOwners: [],
    opportunityTags: [],
    opportunityTypes: [],
    opportunityAmountGreaterThanOrEqual: null,
    opportunityPractices: [],
    currency: null,
    sort: new QuerySort('closeDate', 'asc'),
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        opportunityOpenedPeriod: reportsSearchParamsConfig.opportunityOpenedPeriod,
        opportunityClosedPeriod: reportsSearchParamsConfig.opportunityClosedPeriod,
        companies: searchParamsConfig.companies,
        opportunitySalesforceSynced: searchParamsConfig.opportunitySalesforceSynced,
        opportunityHubspotSynced: searchParamsConfig.opportunityHubspotSynced,
        opportunityStageCategories: searchParamsConfig.opportunityStageCategories,
        opportunityStages: searchParamsConfig.opportunityStages,
        opportunityOwners: searchParamsConfig.opportunityOwners,
        opportunityTags: searchParamsConfig.opportunityTags,
        opportunityTypes: searchParamsConfig.opportunityTypes,
        opportunityAmountGreaterThanOrEqual: searchParamsConfig.opportunityAmountGreaterThanOrEqual,
        opportunityPractices: searchParamsConfig.opportunityPractices,
        currency: searchParamsConfig.currency,
        sort: { default: new QuerySort('closeDate', 'asc'), ...searchParamsConfig.sort },
      }),
      [searchParamsConfig, reportsSearchParamsConfig],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  const integrations = useIntegrations();

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      opportunityOpenedStart: params.opportunityOpenedPeriod?.start ?? undefined,
      opportunityOpenedEnd: params.opportunityOpenedPeriod?.end ?? undefined,
      opportunityClosedStart: params.opportunityClosedPeriod?.start ?? undefined,
      opportunityClosedEnd: params.opportunityClosedPeriod?.end ?? undefined,
      companyId: params.companies.map((v) => v.id),
      opportunitySalesforceSynced:
        integrations?.salesforce && params.opportunitySalesforceSynced ? params.opportunitySalesforceSynced : undefined,
      opportunityHubspotSynced:
        integrations?.hubspot && params.opportunityHubspotSynced ? params.opportunityHubspotSynced : undefined,
      opportunityStageCategoryId: params.opportunityStageCategories.map((v) => v.id),
      opportunityStageId: params.opportunityStages.length ? params.opportunityStages.map((v) => v.id) : [],
      opportunityOwnerId: params.opportunityOwners.map((v) => v.id),
      opportunityTagId: params.opportunityTags.map((v) => v.id),
      opportunityTypeId: params.opportunityTypes.map((v) => v.id),
      opportunityAmountGreaterThanOrEqual: params.opportunityAmountGreaterThanOrEqual ?? undefined,
      opportunityPracticeId: params.opportunityPractices.map((v) => v.id),
      currency: params.currency ?? undefined,
      sort: params.sort,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [params, integrations?.salesforce, integrations?.hubspot],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    if (!isMounted.current) return;

    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: {
        table: false,
      },
    }));
  }, [api, workspace.id, report.id, urlSearchParams, isMounted]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleCurrencyChange = (currency) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, summary: true, table: true } }));
    setParams({ ...params, page: 0, currency });
    searchParams.set({ currency });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        {(params.opportunityOpenedPeriod?.start || params.opportunityOpenedPeriod?.end) && (
          <ReportPeriodFilter
            value={params.opportunityOpenedPeriod}
            placeholder="Opened Date Range"
            onChange={({ target: { value } }) => handleApplyFilters({ opportunityOpenedPeriod: value })}
          />
        )}

        {(params.opportunityClosedPeriod?.start || params.opportunityClosedPeriod?.end) && (
          <ReportPeriodFilter
            value={params.opportunityClosedPeriod}
            placeholder="Expected Close Date Range"
            onChange={({ target: { value } }) => handleApplyFilters({ opportunityClosedPeriod: value })}
          />
        )}

        {features.multicurrency && (
          <CurrencyFilter value={params.currency} onChange={({ target: { value } }) => handleCurrencyChange(value)} />
        )}

        {!_.isEmpty(params.companies) && (
          <CompanyFilter
            name="companies"
            value={params.companies}
            onChange={({ target: { value } }) => handleApplyFilters({ companies: value })}
          />
        )}

        <OpportunityFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} onCurrencyChange={handleCurrencyChange} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  return (
    <Report.Table>
      <Table>
        <Table.Header sticky>
          <Table.Column sticky name="opportunity.name" onSort={onSort} sort={params.sort} minWidth="16rem">
            Opportunity/Company
          </Table.Column>
          <Table.Column name="owner.name" onSort={onSort} sort={params.sort} minWidth="14rem">
            Owner
          </Table.Column>
          <Table.Column name="opportunityStage.name" onSort={onSort} sort={params.sort} width="12rem">
            Stage
          </Table.Column>
          <Table.Column minWidth="10rem">Tags</Table.Column>
          <Table.Column name="convertedAmount" onSort={onSort} sort={params.sort} align="right" width="10rem">
            Amount
          </Table.Column>
          <Table.Column name="probability" onSort={onSort} sort={params.sort} align="right" width="8rem">
            Probability
          </Table.Column>
          <Table.Column name="convertedWeightedAmount" onSort={onSort} sort={params.sort} align="right" width="10rem">
            Weighted
          </Table.Column>
          <Table.Column name="lastActivityDate" onSort={onSort} sort={params.sort} align="right" width="7rem">
            Last Activity
          </Table.Column>
          <Table.Column name="openDate" onSort={onSort} sort={params.sort} align="right" width="7rem">
            Opened
          </Table.Column>
          <Table.Column name="closeDate" onSort={onSort} sort={params.sort} align="right" width="7rem">
            Closing
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {report.records.map((opportunity) => (
            <Table.Row key={opportunity.id}>
              <Table.Cell>
                <InfoContainer>
                  <OpportunityLink opportunity={opportunity} />
                  <small>
                    <CompanyLink company={opportunity.company} />
                  </small>
                </InfoContainer>
              </Table.Cell>
              <Table.Cell>
                {opportunity.owner && (
                  <Owner>
                    <MemberContactPopover member={opportunity.owner} placement="left">
                      <Avatar value={opportunity.owner} isCircle hasBackground initialsFontSize=".9rem" />
                    </MemberContactPopover>
                    <OwnerInfo>{opportunity.owner.name}</OwnerInfo>
                  </Owner>
                )}
              </Table.Cell>
              <Table.Cell>
                <p>
                  {`${opportunity.opportunityStage.name}`}
                  <Byline>{opportunityStageCategories[opportunity.opportunityStage.statusId].name}</Byline>
                </p>
              </Table.Cell>
              <Table.Cell>
                <Tags>
                  <FirstAssignment tags={opportunity.tags} />
                  <Assignments tags={opportunity.tags} />
                </Tags>
              </Table.Cell>
              <Table.Cell>
                <p>
                  <Currency value={opportunity.convertedAmount} currency={report.currency} />
                  {opportunity.currency !== report.currency && (
                    <Byline>
                      <Currency value={opportunity.amount} currency={opportunity.currency} />
                    </Byline>
                  )}
                </p>
              </Table.Cell>
              <Table.Cell>
                <Percent value={opportunity.probability / 100} minimumFractionDigits={0} maximumFractionDigits={0} />
              </Table.Cell>
              <Table.Cell>
                <p>
                  <Currency value={opportunity.convertedWeightedAmount} currency={report.currency} />
                  {opportunity.currency !== report.currency && (
                    <Byline>
                      <Currency value={opportunity.weightedAmount} currency={opportunity.currency} />
                    </Byline>
                  )}
                </p>
              </Table.Cell>
              <Table.Cell>
                <DateTime value={opportunity.lastActivityDate} />
              </Table.Cell>
              <Table.Cell>
                <DateTime value={opportunity.openDate} />
              </Table.Cell>
              <Table.Cell>
                <DateTime value={opportunity.closeDate} />
              </Table.Cell>
            </Table.Row>
          ))}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell />
            <Table.Cell>
              <Currency value={report.totals.convertedAmount} currency={report.currency} />
            </Table.Cell>
            <Table.Cell />
            <Table.Cell>
              <Currency value={report.totals.convertedWeightedAmount} currency={report.currency} />
            </Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell />
          </Table.Row>
        </Table.Body>

        <Table.Status
          total={report.records.length}
          label="Opportunity"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.opportunityOpenedPeriod}
        placeholder="Opened Date Range"
        onChange={({ target: { value } }) => handleFilter({ opportunityOpenedPeriod: value })}
      />

      <FiltersGroup label="Company Filters" filters={[filters.companies]}>
        <CompanyFilter
          name="companies"
          value={filters.companies}
          onChange={({ target: { value } }) => handleFilter({ companies: value })}
        />
      </FiltersGroup>

      <OpportunityFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

export default OpportunityDetail;
